import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface StockMovement {
  id: string;
  item_id: string | null;
  clinic_id: string;
  type: "ENTRADA" | "SAIDA" | "AJUSTE";
  quantity: number;
  quantity_before: number;
  quantity_after: number;
  expiry_date: string | null;
  reason: string | null;
  created_by: string | null;
  created_at: string | null;
  deleted_at?: string | null;
  deleted_by?: string | null;
  items?: {
    id: string;
    name: string;
  };
}

export interface StockMovementInput {
  item_id: string;
  /** Omit to use current user's clinic from profile */
  clinic_id?: string;
  type: "ENTRADA" | "SAIDA" | "AJUSTE";
  quantity: number;
  expiry_date?: string;
  reason?: string;
  created_by?: string;
}

export async function getCurrentClinicId(): Promise<string> {
  const { data: authData, error: authError } = await supabase.auth.getUser();
  if (authError) throw authError;
  const userId = authData?.user?.id;
  if (!userId) throw new Error("Usuário não autenticado");
  const { data: profile, error: profileError } = await supabase
    .from("profiles")
    .select("current_clinic_id")
    .eq("id", userId)
    .single();
  if (profileError) throw profileError;
  if (!profile?.current_clinic_id) throw new Error("Nenhuma clínica selecionada");
  return profile.current_clinic_id;
}

async function updateLatestGeneratedMovementReason({
  clinicId,
  itemId,
  previousQuantity,
  nextQuantity,
  reason,
}: {
  clinicId: string;
  itemId: string;
  previousQuantity: number;
  nextQuantity: number;
  reason?: string;
}) {
  if (!reason?.trim()) return;
  if (previousQuantity === nextQuantity) return;

  const { data: movement } = await supabase
    .from("product_stock_movements")
    .select("id")
    .eq("clinic_id", clinicId)
    .eq("item_id", itemId)
    .eq("quantity_before", previousQuantity)
    .eq("quantity_after", nextQuantity)
    .is("reason", null)
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (!movement?.id) return;

  const { error } = await supabase
    .from("product_stock_movements")
    .update({ reason: reason.trim() })
    .eq("id", movement.id);

  if (error) throw error;
}

export function useStockMovements(
  filters?: {
    clinicId?: string;
    itemId?: string;
  },
  options?: {
    enabled?: boolean;
    limit?: number;
  }
) {
  return useQuery<StockMovement[]>({
    queryKey: ["stock-movements", filters, options?.limit],
    enabled: options?.enabled ?? true,
    queryFn: async () => {
      const queryBuilder = supabase
        .from("product_stock_movements")
        .select(`
          *,
          items:item_id (id, name)
        `)
        .order("created_at", { ascending: false })
        .limit(options?.limit ?? 100);

      let finalQuery = queryBuilder;

      if (filters?.clinicId) {
        finalQuery = finalQuery.eq("clinic_id", filters.clinicId);
      }
      if (filters?.itemId) {
        finalQuery = finalQuery.eq("item_id", filters.itemId);
      }

      const { data, error } = await finalQuery;
      if (error) throw error;
      const rows = (data || []) as unknown as StockMovement[];
      return rows.filter((movement) => movement.deleted_at == null);
    },
  });
}

/**
 * Creates a stock movement by updating product_stock. The DB trigger
 * log_product_stock_movement then inserts into product_stock_movements.
 * Uses product_stock (and product_stock_movements) only; no deprecated stock_movements table.
 */
export async function applyStockMovement(input: StockMovementInput) {
  const clinicId = input.clinic_id ?? (await getCurrentClinicId());
  const { item_id, type, quantity } = input;
  const qty = Number(quantity);
  if (!Number.isFinite(qty) || qty <= 0) {
    throw new Error("Quantidade deve ser um número positivo");
  }

  const { data: existing } = await supabase
    .from("product_stock")
    .select("id, quantity, deleted_at")
    .eq("item_id", item_id)
    .eq("clinic_id", clinicId)
    .maybeSingle();

  const previousQuantity = Number(existing?.quantity ?? 0);
  let newQuantity: number;
  if (type === "ENTRADA") {
    newQuantity = previousQuantity + qty;
  } else if (type === "SAIDA") {
    const current = previousQuantity;
    if (current < qty) throw new Error("Saldo insuficiente para saída");
    newQuantity = current - qty;
  } else {
    newQuantity = qty;
  }

  if (newQuantity < 0) {
    throw new Error("Quantidade resultante não pode ser negativa");
  }

  const payload = {
    item_id,
    clinic_id: clinicId,
    quantity: newQuantity,
    deleted_at: null,
    deleted_by: null,
  };

  if (existing?.id && existing.deleted_at == null) {
    const { data: updated, error } = await supabase
      .from("product_stock")
      .update(payload)
      .eq("id", existing.id)
      .select()
      .single();
    if (error) throw error;
    await updateLatestGeneratedMovementReason({
      clinicId,
      itemId: item_id,
      previousQuantity,
      nextQuantity: newQuantity,
      reason: input.reason,
    });
    return updated;
  }

  const { data: inserted, error } = await supabase
    .from("product_stock")
    .insert(payload)
    .select()
    .single();
  if (error) throw error;
  await updateLatestGeneratedMovementReason({
    clinicId,
    itemId: item_id,
    previousQuantity,
    nextQuantity: newQuantity,
    reason: input.reason,
  });
  return inserted;
}

export function useCreateStockMovement() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: applyStockMovement,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stock-movements"] });
      queryClient.invalidateQueries({ queryKey: ["product-stock"] });
      queryClient.invalidateQueries({ queryKey: ["stock-balances"] });
      toast.success("Movimentação registrada com sucesso!");
    },
    onError: (error: Error) => {
      toast.error(`Erro ao registrar movimentação: ${error.message}`);
    },
  });
}
